import {Accordion, Form, Modal} from "react-bootstrap";
import React, {useEffect, useState} from "react";
import {listService} from "../../services/listsService";
import {useForm} from "react-hook-form";
import FormSelectCustom from "../../shared/components/FormSelectCustom";
import {customerService} from "../../services/customerService";
import {PopoverButton} from "../../shared/components/PopoverButton";
import {DynamicFieldTagDnD} from "../../shared/components/DynamicFieldTagDnD";
import {QuillTextEditor} from "../../shared/components/QuillTextEditor";
import {CustomerSearchType} from "./CustomerSearchForm";
import notificationService from "../../services/NotificationService";
import {EmailTemplateModel, messageTemplateService, SMSTemplateModel} from "../../services/messageTemplateService";
import ConfirmationModal from "../ConfirmationModal";
import {handlebarsToVisual, visualToHandlebars} from "../../shared/utils/handlebarsVisualizer";
import {extractHandlebarTags, findUnknownHandlebarTags, stripHtmlToText} from "../../shared/utils/handlebarsValidation";

interface MessageCustomerModalProps {
    show: boolean,
    toggle: () => void,
    customers: any,
    totalCustomerCount: number,
    searchTerms?: CustomerSearchType
}

const MAX_MESSAGE_LENGTH = 150
const MessageCustomerModal: React.FC<MessageCustomerModalProps> = ({
                                                                       show,
                                                                       toggle,
                                                                       customers,
                                                                       totalCustomerCount,
                                                                       searchTerms
                                                                   }) => {
    const {register, setValue, trigger, reset, getValues, watch, formState: {dirtyFields, errors}} = useForm();
    const [messageHtml, setMessageHTML] = useState('')
    const [templateToDelete, setTemplateToDelete] = useState('')
    const [needsTemplateName, setNeedsTemplateName] = useState(false)
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [smsTemplates, setSMSTemplates] = useState([]);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [templateNames, setTemplateNames] = useState<string[]>([]);
    const [textMessage] = watch(['textMessage']);
    useEffect(() => {
        if (!textMessage) return
        if (textMessage.length > MAX_MESSAGE_LENGTH) {
            setValue('textMessage', textMessage.slice(0, MAX_MESSAGE_LENGTH));
        }
    }, [textMessage]);

    function getTemplateUniqueNames(smsList: Awaited<any>, emailList: Awaited<any>) {
        const allNames = [...smsList.map(i => i.templateName), ...emailList.map(i => i.templateName)];
        return Array.from(new Set(allNames));
    }
    const resetForm = () => {
        const defaultValues = {
            sendEmail: false,
            templateName: '',
            sendSMSMessage: false,
            emailSubject: '',
            textMessage: '',
        }
        setMessageHTML('<p><br></p>')
        reset(defaultValues)
    }

    function getTemplates() {
        messageTemplateService.getBothTemplates().then(r => {
            if (!r) return;
            // r[0] sms. r[1] email
            setSMSTemplates(r[0])
            setEmailTemplates(r[1])
            const listOfNames = getTemplateUniqueNames(r[0],r[1])
            setTemplateNames(listOfNames);
        });
    }

    useEffect(() => {
        resetForm()
        getTemplates();
    }, []);

    const handleSave = () => {
        const data = getValues()
        const calls: Promise<any>[] = [];
        const validation = trigger()
        if(!data.templateName){
            setNeedsTemplateName(true)
            notificationService.error('Template Name is required')
            return;
        }
        else setNeedsTemplateName(false)
        const emailTemplate = emailTemplates.find(x => x.templateName === data.templateName);
        const smsTemplate = smsTemplates.find(x => x.templateName === data.templateName);
        if(data.sendSMSMessage){
            const smsModel = new SMSTemplateModel()
            smsModel.messageText = data.textMessage;
            smsModel.templateName = data.templateName;
            smsModel.description = '';
            
            if(smsTemplate) {
                smsModel.smsMessageTemplateId = smsTemplate.smsMessageTemplateId
                calls.push(messageTemplateService.updateSMSTemplate(smsModel))
            }
            else 
                calls.push(messageTemplateService.createSMSTemplate(smsModel))
        }
        if(data.sendEmail){
            const emailModel = new EmailTemplateModel();
            emailModel.templateName = data.templateName;
            emailModel.description = '';
            emailModel.subject = data.emailSubject;
            emailModel.body = visualToHandlebars(messageHtml);
            emailModel.fromName = 'DONOTREPLY@barrecablacktop.com';
            if(emailTemplate){
                emailModel.emailMessageTemplateId = emailTemplate.emailMessageTemplateId;
                calls.push(messageTemplateService.updateEmailTemplate(emailModel))
            }
            else
                calls.push(messageTemplateService.createEmailTemplate(emailModel))
        }
        if(calls.length === 0){
            notificationService.error('Must have at least one SMS or Email message typed to save template')
            return;
        }
        return Promise.all(calls).then(r => {
            if(!r) return;
            getTemplates()
            notificationService.success('Template(s) saved')
        });
    };
    const handleSend = () => {
        const data = getValues()
        if (data.sendEmail) {
            const emailHtml = visualToHandlebars(messageHtml)
            if (stripHtmlToText(emailHtml).trim() === '' && !/<img/i.test(emailHtml)) {
                notificationService.error('Email body cannot be empty.')
                return
            }
            const unknownTags = Array.from(new Set([
                ...findUnknownHandlebarTags(data.emailSubject ?? ''),
                ...findUnknownHandlebarTags(stripHtmlToText(emailHtml))
            ]))
            if (unknownTags.length > 0) {
                notificationService.error(`Replace or remove these custom tags before sending: ${unknownTags.join(', ')}`)
                return
            }
        }
        if (data.sendSMSMessage) {
            const smsTags = Array.from(new Set(extractHandlebarTags(data.textMessage ?? '')))
            if (smsTags.length > 0) {
                notificationService.error(`Text messages do not support dynamic fields. Remove these tags before sending: ${smsTags.join(', ')}`)
                return
            }
        }
        const postObject = {
            sendEmail: data.sendEmail,
            sendSMSMessage: data.sendSMSMessage,
            emailSubject: data.emailSubject,
            textMessage: data.textMessage,
            emailHTML: visualToHandlebars(messageHtml),
            templateName: data.templateName
        }
        customerService.SendMessaging(searchTerms, postObject).then(r => {
            if(!r) return;
            if(r.recipientCount > 0){
                notificationService.success("Message Sent")
                toggle();
            }
        })
    };
    const customerProperties = [
        {label: 'Customer Name', backendVariable: 'customerName'},
        {label: 'Customer Email', backendVariable: 'customerEmail'},
        {label: 'Customer Home Phone', backendVariable: 'customerHomePhone'},
        {label: 'Customer Work Phone', backendVariable: 'customerWorkPhone'},
        {label: 'Customer Mobile Phone', backendVariable: 'customerMobilePhone'},
        {label: 'Customer Web ID', backendVariable: 'webId'},
    ];
    const propertyProperties = [
        {label: 'Property Address', backendVariable: 'serviceFullAddress'},
        {label: 'Job Date Done', backendVariable: 'jobDateDone'},
        {label: 'Job Amount Charged', backendVariable: 'jobAmountCharged'},

    ];
    const paymentProperties = [
        {label: 'Billing Name', backendVariable: 'billingName'},
        {label: 'Billing Address', backendVariable: 'billingAddress'},
        {label: 'Billing CityStateZip', backendVariable: 'billingCityStateZip'},
    ];
    const jobFields = [
        {label: 'Job Number', backendVariable: 'jobNumber'},
        {label: 'Customer Number', backendVariable: 'customerNumber'},
        {label: 'Service Date', backendVariable: 'serviceDate'},
        {label: 'Date Estimated', backendVariable: 'dateEstimated'},
        {label: 'Sealcoating Price', backendVariable: 'sealcoatingPrice'},
        {label: 'Patchwork Price', backendVariable: 'patchworkPrice'},
        {label: 'Amount Due', backendVariable: 'amountDue'},
        {label: 'Total Price', backendVariable: 'totalPrice'},
        {label: 'Total Price (No Discounts)', backendVariable: 'totalPriceWithNoDiscounts'},
        {label: 'Service Address', backendVariable: 'serviceFullAddress'},
        {label: 'Service City State Zip', backendVariable: 'serviceCityStateZip'},
    ];
    
    const smsMessages = watch('textMessage')

    useEffect(() => {
        if(messageHtml && messageHtml + '' != '<p><br></p>')
            setValue('sendEmail', true);
        else
            setValue('sendEmail', false);
    }, [messageHtml]);

    useEffect(() => {
        if(smsMessages && smsMessages != '')
            setValue('sendSMSMessage', true);
        else
            setValue('sendSMSMessage', false);
            
    }, [smsMessages]);

    function loadTemplate(template: string) {
        resetForm();
        let newValues = {
            sendEmail: false,
            templateName: '',
            sendSMSMessage: false,
            emailSubject: '',
            textMessage: '',
        }
        const emailTemplate = emailTemplates.find(x => x.templateName === template);
        const smsTemplate = smsTemplates.find(x => x.templateName === template);
        newValues.templateName = template;
        if(emailTemplate){
            newValues.sendEmail = true;
            newValues.emailSubject = emailTemplate.subject;
            setMessageHTML(handlebarsToVisual(emailTemplate.body))
        }
        if(smsTemplate){
            newValues.sendSMSMessage = true;
            newValues.textMessage = smsTemplate.messageText;
        }
        reset(newValues)
    }

    async function deleteTemplate(template: string) {
        const emailTemplate = emailTemplates.find(x => x.templateName === template);
        const smsTemplate = smsTemplates.find(x => x.templateName === template);
        const calls: Promise<any>[] = [];
        if (emailTemplate) {
            const id = emailTemplate.emailMessageTemplateId;
            calls.push(messageTemplateService.deleteEmailTemplate(id));
        }
        if (smsTemplate) {
            const smsId = smsTemplate.smsMessageTemplateId;
            calls.push(messageTemplateService.deleteSMSTemplate(smsId))
        }

        const r = await Promise.all(calls);
        if (!r)
            return;
        getTemplates();
        resetForm();
    }

    async function loadBackendTemplate(templateName: string) {
        const response = await messageTemplateService.getBackendTemplate(templateName);
        if (!response) return;
        resetForm();
        setValue('emailSubject', response.defaultSubject);
        setValue('sendEmail', true);
        setMessageHTML(handlebarsToVisual(response.templateHtml));
    }

    return (
        <>
            <Modal
                show={show}
                onHide={toggle}
                id="msgCustomerModal1"
                dialogClassName="modal-fullscreen modal-dialog-scrollable"
                data-bs-keyboard="false"
                tabIndex={-1}
                aria-labelledby="messageFilteredMembers"
                aria-hidden="true"
            >
                <Modal.Header className="d-flex justify-content-between align-items-center border-0 p-4">
                    <div className="d-flex align-items-center">
                        <h3 className="modal-title" id="staticBackdropLabel"><i
                            className="fa-regular fa-users"></i> Messaging {totalCustomerCount} Customers</h3>
                    </div>
                    <button type="button" onClick={toggle} className="btn-close" data-bs-dismiss="modal"
                            aria-label="Close"></button>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <div className="row g-0 h-100">
                        <div className="col-md-5 h-100 ">
                            <div className="overflow-auto position-fixed w-inherit" style={{maxHeight: 'calc(100vh - 130px)'}}>
                                <>
                                    {/* <!-- templates-->*/}
                                    <div
                                        className="d-flex justify-content-between align-items-center bg-light p-3 position-sticky top-0 z-1">
                                        <h6 className="fw-medium ps-1 mb-0"><i
                                            className="fa-regular fa-scribble text-black-50"></i> Dynamic Fields</h6>
                                        <PopoverButton
                                            tooltipContent={"Use dynamic fields to automatically populate user data in emails."}
                                            iTagClass={"fa-solid fa-circle-question opacity-25"}/>
                                    </div>

                                    {/*!--field accordion --*/}
                                    <Accordion className="site-nav" alwaysOpen flush>
                                        <Accordion.Item eventKey="0">
                                            <Accordion.Header>
                                                <span><i
                                                    className="fa-solid fa-users opacity-25 me-2"></i> Customer</span>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                <div className="pb-2 d-flex flex-wrap bg-white">
                                                    {customerProperties.map(x => {
                                                        return <DynamicFieldTagDnD name={x.label}
                                                                                   backendVariableName={x.backendVariable}/>
                                                    })}
                                                </div>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                        <Accordion.Item eventKey="1">
                                            <Accordion.Header>
                                            <span>
                                                <i className="fa-solid fa-home opacity-25 me-2"/>
                                                {' '}Property
                                            </span>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                <div className="pb-2 d-flex flex-wrap bg-white">
                                                    {propertyProperties.map(x => {
                                                        return <DynamicFieldTagDnD name={x.label}
                                                                                   backendVariableName={x.backendVariable}/>
                                                    })}
                                                </div>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                        <Accordion.Item eventKey="2">
                                            <Accordion.Header>
                                            <span>
                                                <i className="fa-solid fa-credit-card opacity-25 me-2"></i> Payment
                                            </span>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                <div className="pb-2 d-flex flex-wrap bg-white">
                                                    {paymentProperties.map(x => {
                                                        return <DynamicFieldTagDnD name={x.label}
                                                                                   backendVariableName={x.backendVariable}/>
                                                    })}
                                                </div>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                        <Accordion.Item eventKey="3">
                                            <Accordion.Header>
                                            <span>
                                                <i className="fa-solid fa-file-invoice opacity-25 me-2"></i> Job / Invoice
                                            </span>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                <div className="pb-2 d-flex flex-wrap bg-white">
                                                    {jobFields.map(x => {
                                                        return <DynamicFieldTagDnD name={x.label}
                                                                                   backendVariableName={x.backendVariable}/>
                                                    })}
                                                </div>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>

                                    {/*!--email designs (backend templates)--*/}
                                    <div
                                        className="d-flex justify-content-between align-items-center bg-light p-3 position-sticky top-0 z-1">
                                        <h6 className="fw-medium ps-1 mb-0"><i
                                            className="fa-regular fa-envelope-open-text text-black-50"></i> Email
                                            Designs</h6>
                                        <PopoverButton
                                            tooltipContent={"Pre-built email designs for invoices and estimates. These match the templates used for individual emails."}
                                            iTagClass={"fa-solid fa-circle-question opacity-25"}/>
                                    </div>
                                    <ul className="list-group list-group-flush templateList">
                                        <li className="list-group-item d-flex align-items-center justify-content-between">
                                            <span>Invoice</span>
                                            <button onClick={() => loadBackendTemplate('invoice')} type="button"
                                                    className="btn btn-sm btn-outline-primary">Use
                                            </button>
                                        </li>
                                        <li className="list-group-item d-flex align-items-center justify-content-between">
                                            <span>Estimate / Proposal</span>
                                            <button onClick={() => loadBackendTemplate('estimate')} type="button"
                                                    className="btn btn-sm btn-outline-primary">Use
                                            </button>
                                        </li>
                                    </ul>

                                    {/*!--templates--*/}
                                    <div
                                        className="d-flex justify-content-between align-items-center bg-light p-3 position-sticky top-0 z-1">
                                        <h6 className="fw-medium ps-1 mb-0"><i
                                            className="fa-regular fa-block-brick text-black-50"></i> Templates</h6>
                                        <PopoverButton
                                            tooltipContent={"Use templates to easily reuse common email and SMS messages"}
                                            iTagClass={"fa-solid fa-circle-question opacity-25"}/>
                                    </div>
                                    <ul className="list-group list-group-flush templateList">
                                        <>
                                            {templateNames.map(template => {
                                                return (
                                                    <li className="list-group-item d-flex align-items-center justify-content-between">
                                                        <span className="">{template}</span>
                                                        <div>
                                                            <button onClick={() => loadTemplate(template)} type="button"
                                                                    className="btn btn-sm btn-outline-primary me-2">Use
                                                            </button>
                                                            <button onClick={() => {
                                                                setTemplateToDelete(template);
                                                                setShowConfirmation(true)
                                                            }} type="button"
                                                                    className="btn btn-sm btn-outline-danger">Delete
                                                            </button>
                                                        </div>
                                                    </li>
                                                )
                                            })}
                                            <li className="list-group-item d-flex align-items-center justify-content-between">
                                                <span className="">+ New Template</span>
                                                <button onClick={resetForm} type="button"
                                                        className="btn btn-sm btn-outline-new">Create
                                                </button>
                                            </li>
                                        </>
                                    </ul>
                                </>
                            </div>
                        </div>
                        <div className="col-md-7 border-start border-light">
                            <div className="px-3 pb-3">
                                <div className="py-3 bg-white border-bottom border-light position-sticky top-0 z-1">
                                    <label htmlFor="templateName" className="form-label fw-bold fs-3 mb-3"><i
                                        className="fa-regular fa-block-brick text-black-50"></i> Template Name</label>
                                    <textarea {...register('templateName', { required: 'This field is required.' })} className={`form-control ${errors.templateName ? 'is-invalid' : ''}`} id="templateName" rows={1}></textarea>
                                    {errors.templateName && (
                                        <div className="invalid-feedback">{errors.templateName.message?.toString()}</div>
                                    )}
                                </div>

                                {/* <!-- hide text if template -->*/}
                                <div className="card card-rounded shadow-none border my-4">
                                    <div className="p-3">
                                        <div className="form-check form-switch d-flex align-items-center">
                                            <input {...register('sendSMSMessage')}
                                                   className="form-check-input form-check-input-lg me-3 mt-0"
                                                   type="checkbox"
                                                   role="switch" id="flexSwitchCheckChecked"/>
                                            <label className="form-check-label" htmlFor="flexSwitchCheckChecked">
                                                <h3 className="fw-semibold mb-0">Send Text Message</h3>
                                            </label>
                                        </div>
                                        <div className="mt-4">
                                            <label htmlFor="exampleFormControlTextarea1"
                                                   className="form-label d-flex justify-content-between">Your
                                                Message <span
                                                    className="opacity-50 fw-normal">{textMessage?.length}/{MAX_MESSAGE_LENGTH} characters</span></label>
                                            <textarea maxLength={MAX_MESSAGE_LENGTH} {...register('textMessage')}
                                                      className="form-control" id="exampleFormControlTextarea1"
                                                      rows={3}></textarea>
                                        </div>
                                    </div>

                                </div>
                                <div className="card card-rounded shadow-none border p-3">
                                    <div className="p-3">
                                        <div className="form-check form-switch d-flex align-items-center">
                                            <input {...register('sendEmail')}
                                                   className="form-check-input form-check-input-lg me-3 mt-0"
                                                   type="checkbox" role="switch" id="flexSwitchCheckChecked"/>
                                            <label className="form-check-label" htmlFor="flexSwitchCheckChecked">
                                                <h3 className="fw-semibold mb-0">Send Email</h3>
                                            </label>
                                        </div>
                                        <div className="mt-4">
                                            <label htmlFor="exampleFormControlTextarea1" className="form-label">Email
                                                Subject</label>
                                            <textarea {...register('emailSubject')} className="form-control"
                                                      id="exampleFormControlTextarea1"
                                                      rows={1}></textarea>
                                        </div>
                                        <div className="mt-4">
                                            <label htmlFor="exampleFormControlTextarea1" className="form-label">Your
                                                Message</label>
                                            <QuillTextEditor value={messageHtml} onChangeHtml={setMessageHTML} typeToAccept={'FIELD'}/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <div className="modal-footer">
                    {/*<!--
                    Save template = enable when a template is open with changes
                    Send = disable if template is open with changes and not saved
                    -->*/}
                    <button type="button" onClick={handleSave} className="btn px-5 py-2 btn-primary"><i className="fa-regular fa-save"></i> Save Template</button>
                    <button type="button" onClick={handleSend} className="btn px-5 py-2 btn-primary"><i className="fa-regular fa-paper-plane-top"></i> Send</button>
                    <button type="button" className="btn px-5 py-2 btn-light me-2" onClick={toggle} data-bs-dismiss="modal">Cancel</button>
                </div>
            </Modal>
            <ConfirmationModal show={showConfirmation} toggle={() => setShowConfirmation(false)} onConfirm={() => deleteTemplate(templateToDelete)}/>
        </>
    );
}
export default MessageCustomerModal;